import { difference, intersection, keyBy, mapValues, uniq, without } from 'es-toolkit';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  CustomTreeData,
  DataTypeProvider,
  IntegratedSelection,
  SelectionState,
  TreeDataState,
} from '@devexpress/dx-react-grid';
import {
  ColumnChooser,
  Grid,
  TableColumnResizing,
  TableColumnVisibility,
  TableHeaderRow,
  TableSelection,
  TableTreeColumn,
  Toolbar,
  VirtualTable,
} from '@devexpress/dx-react-grid-material-ui';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { Box, Paper, Tooltip } from '@mui/material';
import { castDraft } from 'immer';
import { useSearchParams } from 'react-router';

import { expandToFillParentStyles } from '@paragon/client-tools';
import { InlineNotification } from '@paragon/notification-tools';
import { assertExhaustiveSwitch, naturalSortByProperty, notNullish, sortBy } from '@paragon/utilities';

import { loadComponentsForScheduleLineGroup } from '../../../actions/componentActions';
import { getBundlesByScheduleLineGroupGuid } from '../../../selectors/bundles';
import { getDistanceFormatterSelector } from '../../../selectors/getDistanceFormatterSelector';
import { getProductionComponentInstancesByBundleGuid } from '../../../selectors/getProductionComponentInstancesByBundleGuid';
import { getWarningsByBundleGuid } from '../../../selectors/getWarningsByBundleGuid';
import {
  getBundleGuidFromRowId,
  getBundleRowId,
  getLineScheduleGridRowsByLineGuid,
  getScheduleLineGroupGuidFromRowId,
  getScheduleLineGroupRowId,
  isBundleRow,
  isGroupedProductionComponentInstanceRow,
  isScheduleLineGroupRow,
  LineScheduleGridRow,
} from '../../../selectors/lineScheduleGrid';
import {
  checkRows,
  collapseRows,
  expandRows,
  lineGuidFilterChanged,
  setUserColumnWidths,
  uncheckRows,
} from '../../../store/lineScheduleGridSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ComponentPreviewCell } from '../ComponentPreviewCell';
import { CustomTreeCell } from '../CustomTreeCell';
import { ExpandCollapseButtons } from '../ExpandCollapseButtons';
import { GridRoot } from '../GridRoot';
import { Column } from '../gridTypes';
import { compareDates, distancesToString, formatDateToString } from '../gridUtilities';
import { StatusesAsColorBar } from '../StatusesAsColorBar';
import { StatusesAsColoredText } from '../StatusesAsColoredText';
import { LineScheduleGridRowActions } from './LineScheduleGridRowActions';
import { LineScheduleGridToolbarPlugin } from './LineScheduleGridToolbarPlugin';

function rowSelectionDisabled(row: LineScheduleGridRow) {
  const { type } = row;
  switch (type) {
    case 'scheduleLineGroup':
      return false;
    case 'bundle':
      return false;
    case 'groupedProductionComponentInstance':
      return true;
    default:
      assertExhaustiveSwitch(type);
  }
}

const statusColumnWidth = 150;

export function LineScheduleGrid() {
  const dispatch = useAppDispatch();
  const rowsByLine = useAppSelector(getLineScheduleGridRowsByLineGuid);
  const bundlesByScheduleLineGroupGuid = useAppSelector(getBundlesByScheduleLineGroupGuid);
  const productionComponentInstancesByBundleGuid = useAppSelector(getProductionComponentInstancesByBundleGuid);
  const stations = useAppSelector((state) => state.stations);
  const checkedRows = useAppSelector((state) => state.lineScheduleGrid.checkedRows);
  const userColumnWidths = useAppSelector((state) => state.lineScheduleGrid.userColumnWidths);
  const expandedRows = useAppSelector((state) => state.lineScheduleGrid.expandedRows);
  const reduxLineGuidFilter = useAppSelector((state) => state.lineScheduleGrid.lineGuidFilter);
  const warningsByBundleGuid = useAppSelector(getWarningsByBundleGuid);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  const warningShortMessagesByScheduleLineGroupGuid = useMemo(
    () =>
      mapValues(bundlesByScheduleLineGroupGuid, (bundlesForScheduleLineGroup) =>
        uniq(
          bundlesForScheduleLineGroup.flatMap((bundle) =>
            warningsByBundleGuid[bundle.guid].map((bundleWarning) => bundleWarning.shortMessage),
          ),
        ),
      ),
    [bundlesByScheduleLineGroupGuid, warningsByBundleGuid],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const urlLineGuidFilter = searchParams.get('lineGuid');
  function setLineGuidFilter(lineGuidFilter: string) {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.set('lineGuid', lineGuidFilter);
    setSearchParams(updatedSearchParams);
    dispatch(lineGuidFilterChanged(lineGuidFilter));
  }
  useEffect(() => {
    // Switching tabs causes the query parameter specifying the selected line to be lost. We store it in Redux to preserve
    // this value between tab changes. If needed, apply that value here.
    if (urlLineGuidFilter === null && reduxLineGuidFilter !== null) {
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.set('lineGuid', reduxLineGuidFilter);
      setSearchParams(updatedSearchParams);
    }
    // If the page was loaded on this tab with a line GUID in the URL, inform Redux of what the lineGuidFilter is.
    if (urlLineGuidFilter !== null && reduxLineGuidFilter === null) {
      dispatch(lineGuidFilterChanged(urlLineGuidFilter));
    }
  });
  const lineGuidFilter = urlLineGuidFilter ?? reduxLineGuidFilter;

  const rows = useMemo(
    () => (lineGuidFilter === null ? [] : (rowsByLine[lineGuidFilter] ?? [])),
    [rowsByLine, lineGuidFilter],
  );

  const rowsKeyedByRowId = useMemo(() => keyBy(rows, (row) => row.rowId), [rows]);

  const currentProductionComponentInstanceGuidToStationNames = useMemo(
    () =>
      Object.values(stations).reduce(
        (map, station) => {
          if (station.currentProductionComponentInstanceGuid === null) return map;
          if (map[station.currentProductionComponentInstanceGuid] === undefined)
            map[station.currentProductionComponentInstanceGuid] = [];
          map[station.currentProductionComponentInstanceGuid].push(station.name);
          return map;
        },
        {} as { [currentProductionComponentInstanceGuid: string]: string[] },
      ),
    [stations],
  );

  const getDescendantRowIds = useCallback(
    (rowId: string): string[] => {
      if (isScheduleLineGroupRow(rowId)) {
        const scheduleLineGroupGuid = getScheduleLineGroupGuidFromRowId(rowId);
        const bundles = bundlesByScheduleLineGroupGuid[scheduleLineGroupGuid];
        const bundleGuids = bundles.map((bundle) => bundle.guid);
        const groupedProductionComponentInstanceRowIds = rows
          .filter((row) => row.type === 'groupedProductionComponentInstance' && bundleGuids.includes(row.bundleGuid))
          .map((row) => row.rowId);
        return bundleGuids.map(getBundleRowId).concat(groupedProductionComponentInstanceRowIds);
      }
      if (isBundleRow(rowId)) {
        const bundleGuid = getBundleGuidFromRowId(rowId);
        return rows
          .filter((row) => row.type === 'groupedProductionComponentInstance' && row.bundleGuid === bundleGuid)
          .map((row) => row.rowId);
      }

      return [];
    },
    [rows, bundlesByScheduleLineGroupGuid],
  );

  /** This function exists to determine ancestor rows that should be de/selected. They should be de/selected if all
   * siblings are selected. */
  function getAncestorRowIds(rowId: string): string[] {
    function getAffectedAncestorsForBundleRowOrEmptyArray(bundleRowId: string) {
      const bundleRow = rowsKeyedByRowId[bundleRowId];
      if (bundleRow === undefined || bundleRow.type !== 'bundle') throw new Error('Row not found or is wrong type');
      const siblingRowIds = without(
        bundlesByScheduleLineGroupGuid[bundleRow.scheduleLineGroupGuid].map((bundle) => bundle.guid),
        bundleRow.guid,
      ).map((bundleGuid) => getBundleRowId(bundleGuid));
      if (siblingRowIds.every((siblingRowId) => checkedRows.includes(siblingRowId))) {
        return [getScheduleLineGroupRowId(bundleRow.scheduleLineGroupGuid)];
      }
      return [];
    }

    if (isBundleRow(rowId)) {
      return getAffectedAncestorsForBundleRowOrEmptyArray(rowId);
    }

    if (isGroupedProductionComponentInstanceRow(rowId)) {
      const groupedProductionComponentInstanceRow = rowsKeyedByRowId[rowId];
      if (
        groupedProductionComponentInstanceRow === undefined ||
        groupedProductionComponentInstanceRow.type !== 'groupedProductionComponentInstance'
      )
        throw new Error('Row not found or is wrong type');
      const { bundleGuid } = groupedProductionComponentInstanceRow;
      const siblingRowIds = rows
        .filter(
          (row) =>
            row.type === 'groupedProductionComponentInstance' && row.rowId !== rowId && row.bundleGuid === bundleGuid,
        )
        .map((row) => row.rowId);
      if (siblingRowIds.every((siblingRowId) => checkedRows.includes(siblingRowId))) {
        const bundleRowId = getBundleRowId(bundleGuid);
        return [bundleRowId].concat(getAffectedAncestorsForBundleRowOrEmptyArray(bundleRowId));
      }
    }

    return [];
  }

  function rowSelectionIndeterminate(checkedRows: readonly string[]) {
    return function rowSelectionIndeterminateWithCheckedRows(row: LineScheduleGridRow): boolean {
      const { rowId, type } = row;
      if (checkedRows.includes(rowId) || type === 'groupedProductionComponentInstance') return false;

      const descendantRowIds = getDescendantRowIds(rowId);
      const numberOfCheckedDescendantRowIds = intersection(checkedRows, descendantRowIds).length;
      return numberOfCheckedDescendantRowIds > 0 && numberOfCheckedDescendantRowIds < descendantRowIds.length;
    };
  }

  const columns: readonly Column<LineScheduleGridRow>[] = useMemo(
    () => [
      {
        name: 'expandCollapseByScheduleLineGroup',
        title: 'Exp/Col',
        defaultWidth: 70,
        sortingEnabled: false,
        filteringEnabled: false,
        getCellValue: (row) => {
          if (row.type !== 'scheduleLineGroup') return null;
          const expandableDescendantRowIds = getDescendantRowIds(row.rowId);
          return (
            <ExpandCollapseButtons
              rowId={row.rowId}
              expandRows={(rowIds) => {
                dispatch(expandRows(rowIds));
                dispatch(loadComponentsForScheduleLineGroup(row.guid));
              }}
              collapseRows={(rowIds) => void dispatch(collapseRows(rowIds))}
              expandableDescendantRowIds={expandableDescendantRowIds}
              expandedRowIds={expandedRows}
            />
          );
        },
      },
      {
        name: 'name',
        title: 'Name',
        defaultWidth: 500,
        sortingEnabled: true,
        filteringEnabled: true,
      },
      {
        name: 'actions',
        title: 'Actions',
        defaultWidth: 150,
        sortingEnabled: false,
        filteringEnabled: false,
        getCellValue: (row) => <LineScheduleGridRowActions row={row} rows={rows} />,
      },
      {
        name: 'scheduledDate',
        title: 'Prod. Date',
        sortingEnabled: true,
        sortingCompare: compareDates,
        filteringEnabled: true,
        defaultWidth: 100,
        getCellValue: (row) => {
          const { scheduledDate } = row;
          if (scheduledDate === null) return null;
          return formatDateToString(scheduledDate);
        },
      },
      {
        name: 'trussCount',
        title: 'Truss Count',
        sortingEnabled: true,
        filteringEnabled: true,
        defaultWidth: 120,
        align: 'right',
      },
      {
        name: 'boardFeet',
        title: 'BF',
        sortingEnabled: true,
        filteringEnabled: true,
        defaultWidth: 100,
        align: 'right',
        getCellValue: (row) => (row.boardFeet === null ? null : Math.round(row.boardFeet)),
      },
      {
        name: 'linearFeet',
        title: 'LF',
        sortingEnabled: true,
        filteringEnabled: true,
        defaultWidth: 100,
        align: 'right',
        getCellValue: (row) => (row.linearFeet === null ? null : Math.round(row.linearFeet)),
      },
      {
        name: 'thicknesses',
        title: 'Thickness',
        defaultWidth: 110,
        align: 'right',
        sortingEnabled: false,
        filteringEnabled: false,
        getCellValue: (row) => distancesToString(row.thicknesses, distanceFormatter),
      },
      {
        name: 'heights',
        title: 'Height',
        sortingEnabled: false,
        filteringEnabled: false,
        defaultWidth: 90,
        align: 'right',
        getCellValue: (row) => distancesToString(row.heights, distanceFormatter),
      },
      {
        name: 'widths',
        title: 'Width',
        sortingEnabled: false,
        filteringEnabled: false,
        defaultWidth: 90,
        align: 'right',
        getCellValue: (row) => distancesToString(row.widths, distanceFormatter),
      },
      {
        name: 'preview',
        title: 'Preview',
        sortingEnabled: false,
        filteringEnabled: false,
        defaultWidth: 100,
        getCellValue: (row) =>
          row.type === 'groupedProductionComponentInstance' ? (
            <ComponentPreviewCell analysisSetGuid={row.analysisSetGuid} maxWidth={100} maxHeight={25} />
          ) : null,
      },
      {
        name: 'status',
        title: 'Status',
        sortingEnabled: false,
        filteringEnabled: false,
        defaultWidth: statusColumnWidth,
        getCellValue: (row) =>
          row.type === 'scheduleLineGroup' ? (
            <StatusesAsColorBar
              statusPercentages={row.statusPercentages}
              statusColumnWidth={statusColumnWidth}
              rowId={row.rowId}
            />
          ) : (
            <StatusesAsColoredText statusPercentages={row.statusPercentages} rowId={row.rowId} />
          ),
      },
      {
        name: 'atStation',
        title: 'At Station',
        sortingEnabled: true,
        filteringEnabled: true,
        defaultWidth: 120,
        getCellValue: (row) => {
          const { type } = row;
          switch (type) {
            case 'scheduleLineGroup':
              return null;
            case 'bundle':
              const productionComponentInstanceGuids = productionComponentInstancesByBundleGuid[row.guid].map(
                (productionComponentInstance) => productionComponentInstance.guid,
              );
              return uniq(
                productionComponentInstanceGuids
                  .flatMap((instanceGuid) => currentProductionComponentInstanceGuidToStationNames[instanceGuid])
                  .filter(notNullish),
              ).join(', ');
            case 'groupedProductionComponentInstance':
              const productionComponentInstanceGuidsWithMatchingAnalysisSetGuid =
                productionComponentInstancesByBundleGuid[row.bundleGuid].filter(
                  (productionComponentInstance) => productionComponentInstance.analysisSetGuid === row.analysisSetGuid,
                );
              return uniq(
                productionComponentInstanceGuidsWithMatchingAnalysisSetGuid
                  .flatMap(
                    (productionComponentInstance) =>
                      currentProductionComponentInstanceGuidToStationNames[productionComponentInstance.guid],
                  )
                  .filter(notNullish),
              ).join(', ');
            default:
              assertExhaustiveSwitch(type);
          }
        },
      },
    ],
    [
      dispatch,
      expandedRows,
      currentProductionComponentInstanceGuidToStationNames,
      distanceFormatter,
      getDescendantRowIds,
      productionComponentInstancesByBundleGuid,
      rows,
    ],
  );

  function onExpandedRowsChange(newExpandedRows: (string | number)[]) {
    for (const newlyCollapsedRow of difference(expandedRows, newExpandedRows)) {
      dispatch(collapseRows([newlyCollapsedRow.toString()]));
    }

    for (const newlyExpandedRow of difference(newExpandedRows, expandedRows)) {
      const newlyExpandedRowString = newlyExpandedRow.toString();
      if (isScheduleLineGroupRow(newlyExpandedRowString)) {
        dispatch(loadComponentsForScheduleLineGroup(getScheduleLineGroupGuidFromRowId(newlyExpandedRowString)));
      }
      dispatch(expandRows([newlyExpandedRowString]));
    }
  }

  function onCheckedRowsChange(newCheckedRows: (string | number)[]) {
    for (const uncheckedRowStringOrNumber of difference(checkedRows, newCheckedRows)) {
      const uncheckedRowId = uncheckedRowStringOrNumber.toString();

      if (rowSelectionDisabled(rowsKeyedByRowId[uncheckedRowId])) continue;

      dispatch(
        uncheckRows(
          [uncheckedRowId].concat(getDescendantRowIds(uncheckedRowId)).concat(getAncestorRowIds(uncheckedRowId)),
        ),
      );
    }

    for (const newlyCheckedRowStringOrNumber of difference(newCheckedRows, checkedRows)) {
      const newlyCheckedRowId = newlyCheckedRowStringOrNumber.toString();

      if (rowSelectionDisabled(rowsKeyedByRowId[newlyCheckedRowId])) continue;

      dispatch(
        checkRows(
          [newlyCheckedRowId]
            .concat(getDescendantRowIds(newlyCheckedRowId))
            .concat(getAncestorRowIds(newlyCheckedRowId)),
        ),
      );
    }
  }

  return (
    <Paper sx={{ flex: 2, ...expandToFillParentStyles.containerStyleProps }}>
      <Grid rows={rows} columns={columns} getRowId={(row) => row.rowId} rootComponent={GridRoot}>
        <DataTypeProvider
          for={['name']}
          formatterComponent={({ row }) => {
            const { type, guid, name } = row as LineScheduleGridRow;
            switch (type) {
              case 'scheduleLineGroup':
                return warningShortMessagesByScheduleLineGroupGuid[guid].length > 0 ? (
                  <Box style={{ display: 'flex', alignItems: 'center' }}>
                    <ReportProblemIcon sx={{ mr: 1 }} />
                    <span>{name}</span>
                    {naturalSortByProperty(warningShortMessagesByScheduleLineGroupGuid[guid], (message) => message).map(
                      (warningShortMessage) => (
                        <InlineNotification sx={{ my: 0, ml: 1 }} type="error" key={warningShortMessage}>
                          {warningShortMessage}
                        </InlineNotification>
                      ),
                    )}
                  </Box>
                ) : (
                  <span>{name}</span>
                );
              case 'bundle':
                return warningsByBundleGuid[guid].length > 0 ? (
                  <Box style={{ display: 'flex', alignItems: 'center' }}>
                    <span>{name}</span>
                    {naturalSortByProperty(warningsByBundleGuid[guid], (warning) => warning.shortMessage).map(
                      (warning) => (
                        <Tooltip
                          disableInteractive
                          sx={{ ml: 1 }}
                          title={warning.longMessage}
                          key={warning.longMessage}
                        >
                          <span>
                            <InlineNotification sx={{ my: 0, ml: 1 }} type="error">
                              {warning.shortMessage}
                            </InlineNotification>
                          </span>
                        </Tooltip>
                      ),
                    )}
                  </Box>
                ) : (
                  <span>{name}</span>
                );
              case 'groupedProductionComponentInstance':
                return <span>{name}</span>;
              default:
                assertExhaustiveSwitch(type);
            }
          }}
        />

        <TreeDataState expandedRowIds={castDraft(expandedRows)} onExpandedRowIdsChange={onExpandedRowsChange} />

        <SelectionState selection={castDraft(checkedRows)} onSelectionChange={onCheckedRowsChange} />
        <IntegratedSelection />

        <CustomTreeData
          getChildRows={(currentRow, rootRows) => {
            if (!currentRow) {
              return sortBy(
                rootRows.filter((row) => row.type === 'scheduleLineGroup'),
                (row) => row.priority,
              );
            }
            if (currentRow.type === 'scheduleLineGroup') {
              const childRows = sortBy(
                rootRows.filter((row) => row.type === 'bundle' && row.scheduleLineGroupGuid === currentRow.guid),
                (bundleRow) => bundleRow.priority,
              );
              return childRows.length > 0 ? childRows : null;
            }
            if (currentRow.type === 'bundle') {
              const childRows = sortBy(
                rootRows.filter(
                  (row) => row.type === 'groupedProductionComponentInstance' && row.bundleGuid === currentRow.guid,
                ),
                (groupedProductionComponentInstanceRow) => groupedProductionComponentInstanceRow.priority,
              );
              return childRows.length > 0 ? childRows : null;
            }
            return null;
          }}
        />

        <VirtualTable
          messages={{ noData: lineGuidFilter ? 'No data' : 'Select a line' }}
          columnExtensions={columns.map((column) => ({ columnName: column.name, align: column.align }))}
        />
        <TableColumnResizing
          columnWidths={
            userColumnWidths ?? columns.map((column) => ({ columnName: column.name, width: column.defaultWidth }))
          }
          onColumnWidthsChange={(newUserColumnWidths) => void dispatch(setUserColumnWidths(newUserColumnWidths))}
        />
        <TableHeaderRow />
        <TableColumnVisibility defaultHiddenColumnNames={['scheduledDate']} />
        <Toolbar />
        <LineScheduleGridToolbarPlugin lineGuidFilter={lineGuidFilter} setLineGuidFilter={setLineGuidFilter} />
        <ColumnChooser />
        <TableTreeColumn
          for="name"
          showSelectionControls
          cellComponent={(props) => (
            <CustomTreeCell
              rowSelectionDisabled={rowSelectionDisabled}
              rowSelectionIndeterminate={rowSelectionIndeterminate(checkedRows)}
              {...props}
            />
          )}
        />
        <TableSelection selectByRowClick showSelectionColumn={false} />
      </Grid>
    </Paper>
  );
}
